import * as React from 'react';
import classNames from 'classnames';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

/**
  @typedef {{
    key: string;
    name: string;
    type: 'string' | 'number' | 'integer' | 'u8' | 'i8' | 'u16' | 'i16' | 'u32' | 'i32' | 'option';
    options?: {
      name: string;
      value: any;
    }[];
    default?: any;
    optional?: boolean;
    validate?: (value: any) => boolean | string;
    halfWidth?: boolean;
  }} Arg
**/

/**
 * This function checks the corresponding argument value based on the args config in the downlink.
 * @param {Arg} arg
 * @param {any} argValue
 * @returns {boolean | string}
 */
export function validateArg(arg, argValue) {
  if (argValue === undefined) {
    if (arg.optional !== true) {
      return 'This argument is required.';
    } else {
      return true;
    }
  } else if (typeof arg.validate === 'function') {
    return arg.validate(argValue);
  } else {
    return true;
  }
}

/**
 * @param {object} options
 * @param {Arg} options.arg
 * @param {any} options.argValue
 * @param {(value: any) => void} options.setArgValue
 * @returns
 */
export function makeOption({ arg, argValue, setArgValue }) {
  const validateResult = validateArg(arg, argValue);
  const isArgValueValid = (validateResult === true);

  return (
    <div
      key={arg.key}
      className={classNames("flex flex-column gap-1", arg.halfWidth ? "col-6" : "col-12")}
    >
      <label className="white-space-nowrap">
        {arg.name ? <span>{arg.name}</span> : <code>{arg.key}</code>}
      </label>
      {
        arg.type === 'string' ? (
          <InputText
            value={argValue}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.target.value || undefined)}
          />
        ) :
        arg.type === 'number' ? (
          <InputNumber
            value={argValue}
            maxFractionDigits={20}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'integer' ? (
          <InputNumber
            value={argValue}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'u8' ? (
          <InputNumber
            value={argValue}
            min={0} max={255}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'i8' ? (
          <InputNumber
            value={argValue}
            min={-128} max={+127}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'u16' ? (
          <InputNumber
            value={argValue}
            min={0} max={65535}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'i16' ? (
          <InputNumber
            value={argValue}
            min={-32768} max={+32767}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'u32' ? (
          <InputNumber
            value={argValue}
            min={0} max={4294967295}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'i32' ? (
          <InputNumber
            value={argValue}
            min={-2147483648} max={+2147483647}
            maxFractionDigits={0}
            useGrouping={false}
            invalid={!isArgValueValid}
            onChange={(event) => setArgValue(event.value ?? undefined)}
          />
        ) :
        arg.type === 'option' ? (
          <Dropdown
            value={argValue}
            options={arg.options}
            optionLabel="name"
            optionValue="value"
            invalid={!isArgValueValid}
            showClear
            onChange={(event) => setArgValue(event.value)}
          />
        ) :
        (
          <InputText
            value={`Invalid argument type: ${arg.type}`}
            variant="filled"
            invalid
            disabled
          />
        )
      }
      {!isArgValueValid ? (
        <small style={{ color: 'red' }}>
          {typeof validateResult === 'string' ? validateResult : 'Invalid value.'}
        </small>
      ) : null}
    </div>
  );
}
